import {BaseModel} from '@/core/standard/BaseModel';
import {IRequestModel, IRequests, RequestProtocol} from '@/modules/requests/requests/IRequestModel';
import RequestService from '@/modules/requests/requests/RequestService';
import {GenericMethod} from '@/core/Types/IRepository';
import GlobalConfigs from '@/setup/GlobalConfigs/GlobalConfigs';
import {IModifierModel} from '@/modules/modifiers/IModifierModel';

const requetsTypes = {
  domicilio: 'domicile',
  local: 'local',
  recoger: 'pickup',
  barra: 'barra',
};


const request = {
  qr: '',
  products: [],
  all_discounted: false,
  type: 'local',
  client: '',
  details: '',
  discount: {value: '', type: ''},
  invalidated_by: '',
  status: 'solicitado',
  process_status: 'solicited',
  from_client: 'web',
  created_at: '',
  updated_at: '',
  is_accepted: '',
  credit_card: false,
  command_guests: 1,
  elapse_time: 0,
  address: null,
  worker: '',
  dealer: '',
  shift_time: '',
  config: null,
  soon_as_possible: true,
  delivery_date: '',
  tip_amount: null,
  tip_option: '',
  promotion: null,
};

class RequestModel extends BaseModel<IRequestModel, RequestProtocol, typeof RequestService> {
  public modelName = 'Pedido';

  constructor() {
    super(request, RequestService);
  }

  public async mapRow(row: IRequestModel & { id: string | number }) {
    const fetchedRow = await this.service.getBy({query: {}, id: row.id});
    const {complete, data} = fetchedRow;
    if (complete) {
      this.mapRequestData(data, row);
      super.mapRow(data);
    }
  }

  public async mapToReports(row: IRequestModel & { id: string | number }) {
    const fetchedRow = await this.service.retrieveReport({query: {}, id: row.id});
    const {complete, data} = fetchedRow;
    if (complete) {
      this.mapRequestData(data, row);
      super.mapRow(data);
    }
  }

  public mapRequestData(data, row: IRequestModel & { id: string | number }) {
    if (data.products.length) {
      data.products.forEach((product) => {
        product.uniqueKey = `${product.product_name}${Math.floor((Math.random() * 100000) + 1)}`;
        product.name = product.product_name;
        product.price = Number(+product.importe / +product.qty).toFixed(2);
      });
    }
    if (!data.discount) {
      data.discount = {value: 0, type: ''};
    }
    data.invalidated_by = row.invalidated_by;
  }

  public async closeRequest(method: string, data: JSON) {
    const response = await this.service.changeStatus({method: 'POST', query: data});
    if (response.complete) {
      return response;

    }
    return false;
  }

  public async print(method: string, data: string, toast) {
    try {
      const response = await this.service.print({method: 'POST', query: data});
      if (response.complete) {
        this.handleSuccess(response, response.message, toast);
        return response;
      }
    } catch (e) {
      this.handleError(e as any, toast);
      return false;
    }
  }

  public async invalidate(id: string) {
    return await this.service.invalidate({method: 'POST', query: JSON.stringify({id})});
  }

  public async mark(id: string, processStatus: string) {
    return await this.service.mark({method: 'POST', query: JSON.stringify({ id, process_status: processStatus })});
  }

  public async markOption({ requestItem, optionId, rimId, processStatus }) {
    return await this.service.markOption({
       method: 'POST',
       query: JSON.stringify({
        list_options: [{ request_item_id: requestItem, option_id: optionId, rim_id: rimId }],
        process_status: processStatus
      })
    });
  }

  public async getDelivery(query) {
    const {data} = await this.service.getDelivery({method: 'get', query: query});
    return data;
  }

  public async acceptInvalidateOrder({id, isAccepted, orderTimeDelay}) {
    let query: any = {
      id,
      is_accepted: isAccepted,
    };
    if (isAccepted) {
      query.order_time_delay = orderTimeDelay;
    }
    const {data} = await this.service.acceptInvalidateOrder({method: 'POST', query});
    return data;
  }

  public async checkRequest(id) {
    return await this.service.checkRequest({
      method: 'POST',
      query: {id},
    });
  }

  public async changeProcessStatus(id: string, processStatus: string) {
    return await this.service.changeProcessStatus({
      method: 'POST',
      query: JSON.stringify({id, process_status: processStatus}),
    });
  }

  public async getClientOrUser(data) {
    return await this.service.getClientOrUser({method: 'GET', ...data});
  }

  public async closeAllDomicilePickup() {
    return await this.service.closeAllDomicilePickup({method: 'POST'});
  }

  public async sendTicketByEmailDomicilePickup(data: any, toast) {
    const response = await this.service.sendTicketByEmailDomicilePickup({method: 'POST', query: data});
    if (response.complete) {
      this.handleSuccess(response, response.message, toast);
      return response;
    }
    this.handleError(response, toast);
    return {response: [], total: 0};
  }

  public mapProduct(product) {
    return {
      id: product.id,
      qty: product.qty,
      name: product.name,
      items: product.items,
      price: product.price,
      modifiers: product.modifiers,
      order: product.order,
      details: product.details,
      is_discounted: product.is_discounted,
      request_item: product.request_item,
    };
  }

  public mapItem(item) {
    return {
      id: item.id,
      name: item.name,
      qty: item.qty,
      price: item.price,
      type: item.type,
      unit_name: item.unit_name,
      unit_value: item.unit_value,
    };
  }

  public mapProducts(products) {
    let productsToSend: any[] = [];
    products.forEach(element => {
      const product = this.mapProduct(element);
      if (GlobalConfigs.use_modifiers && product.modifiers?.length) {
        product.modifiers = this.mapModifiers(product.modifiers);
      }

      if (!GlobalConfigs.use_modifiers && product.items?.length) {
        product.items = product.items.map((item) => this.mapItem(item));
      }
      productsToSend.push(product);
    });
    return productsToSend;
  }

  public mapModifiers(modifiers: IModifierModel[]) {
    return modifiers.filter((modifier) => {
     return (modifier.options = modifier.options.filter((option) => {
        if (option.modifiers && option.modifiers.length) {
          option.modifiers = this.mapModifiers(option.modifiers);
        }
        return option.selected;
      })).length;
    });
  }

  public async create(data, toast: any = null, toastConfig: any = {}) {
    const parsedData = JSON.parse(data);
    const requestType = parsedData.type;
    if (requestType === 'local') {
      delete parsedData.soon_as_possible;
    }
    delete parsedData.type;
    let productsMapped = this.mapProducts(parsedData.products);
    parsedData.products = productsMapped;

    const query = {
      query: parsedData,
      url: requetsTypes[requestType],
      method: 'POST',
    };
    const response = await this.service.insert(query as GenericMethod);
    if (response.complete) {
      return this.handleSuccess(response.data, response.message, toast, toastConfig);
    } else {
      this.handleError(response, toast, toastConfig);
      return false;
    }
  }

  public async update(data, toast: any = null, toastConfig: any = {}) {
    const parsedData = JSON.parse(data);
    const requestType = parsedData.type;
  
    let productsMapped = this.mapProducts(parsedData.products);
    parsedData.products = productsMapped;
    const query = {
      query: parsedData,
      url: `${requetsTypes[requestType]}/${parsedData.id}`,
      method: 'PUT',
    };

    const response = await this.service.update(query as GenericMethod);
    if (response.complete) {
      return this.handleSuccess(response.data, response.message, null);
    } else {
      this.handleError(response, toast, toastConfig);
      return false;
    }
  }
}

export default RequestModel as new() => IRequests;
